import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";

const CTASection = () => {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2642B2] via-indigo-900 to-slate-900"></div>
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${1 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto text-center">
        {/* Top Badge */}
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 backdrop-blur-sm px-6 py-2 rounded-full border border-white/20 mb-8">
          <Icon name="Sparkles" size={18} className="text-cyan-400" />
          <span className="text-white text-sm font-semibold tracking-wider">
            TRANSFORM WITH US
          </span>
          <Icon name="Sparkles" size={18} className="text-blue-400" />
        </div>

        {/* Main Heading */}
        <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight">
          Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-blue-400">Transform</span>
          <br />
          Your Digital Presence?
        </h2>

        {/* Description */}
        <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-10 md:mb-12 leading-relaxed">
          Join <span className="text-cyan-300 font-semibold">196+ satisfied clients</span> who trust Orator Hype for their IT and 
          branding needs. Let's build something <span className="text-blue-300 font-semibold">amazing together</span>.
        </p>

        {/* Stats Bar */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-12">
          {[
            { value: "13+", label: "Years Experience", icon: "Award", color: "text-cyan-400" },
            { value: "196+", label: "Happy Clients", icon: "Users", color: "text-blue-400" },
            { value: "24/7", label: "Support", icon: "Clock", color: "text-blue-400" },
            { value: "100%", label: "Security", icon: "Shield", color: "text-blue-400" },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat.color.replace('text-', '').split('-')[0]}-500/20 flex items-center justify-center`}>
                  <Icon name={stat.icon} size={20} className={stat.color} />
                </div>
                <div className={`text-3xl md:text-4xl font-bold ${stat.color}`}>
                  {stat.value}
                </div>
              </div>
              <div className="text-white/70 text-sm font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center mb-12">
          <Button
            variant="default"
            size="xl"
            onClick={() => handleNavigation("/contact")}
            iconName="MessageCircle"
            iconPosition="right"
            className="group bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white border-0 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 min-w-[220px]"
          >
            <span className="flex items-center gap-2">
              Get Free Consultation
              <Icon name="ArrowRight" size={20} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </Button>
          
          <Button
            variant="outline"
            size="xl"
            onClick={() => handleNavigation("/services")}
            iconName="Layers"
            iconPosition="left"
            className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 hover:border-white/50 hover:scale-105 transition-all duration-300 min-w-[200px]"
          >
            Explore All Services
          </Button>
        </div>

        {/* Trust Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto">
          {[
            { icon: "Zap", text: "Fast Deployment", color: "from-yellow-500/20 to-orange-500/20" },
            { icon: "Lock", text: "GDPR Compliant", color: "from-green-500/20 to-emerald-500/20" },
            { icon: "Globe", text: "Global Reach", color: "from-blue-500/20 to-cyan-500/20" },
            { icon: "TrendingUp", text: "Proven Results", color: "from-blue-500/20 to-blue-500/20" },
          ].map((badge, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${badge.color} backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-white/30 transition-all duration-300 hover:scale-105`}
            >
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Icon name={badge.icon} size={20} className="text-white" />
                </div>
                <span className="text-white text-sm font-medium text-center">
                  {badge.text}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 pt-8 border-t border-white/20">
          <p className="text-white/70 text-base md:text-lg mb-4">
            Have questions? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="flex items-center gap-3">
              <Icon name="Phone" size={20} className="text-cyan-400" />
              <span className="text-white font-semibold">+91 73046 60050</span>
            </div>
            <div className="hidden sm:block text-white/30">|</div>
            <div className="flex items-center gap-3">
              <Icon name="Mail" size={20} className="text-blue-400" />
              <span className="text-white font-semibold">sales@oratorhype.com</span>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .floating {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default CTASection;